import { useState, useCallback, useMemo } from 'react';

export interface AvatarOption {
  id: string;
  seed: string;
  url: string;
  label: string;
}

/**
 * Hook for generating and managing Dicebear Adventurer avatars
 * Uses the Dicebear HTTP API with the Adventurer style
 */
export function useAvatarGenerator() {
  const [selectedAvatarId, setSelectedAvatarId] = useState<string>('');

  // Generate avatar URL using Dicebear API
  const generateAvatarUrl = useCallback((seed: string): string => {
    const baseUrl = 'https://api.dicebear.com/7.x/adventurer/svg';
    const params = new URLSearchParams({
      seed,
      size: '120',
      backgroundColor: 'transparent',
      // Optional: Add more customization
      radius: '20'
    });
    
    return `${baseUrl}?${params.toString()}`;
  }, []);

  // Generate avatar options using different seeds
  const avatarOptions: AvatarOption[] = useMemo(() => {
    const seeds = [
      'Felix', 'Aneka', 'Princess', 'Trouble', 'Midnight', 'Shadow',
      'Luna', 'Whiskers', 'Mittens', 'Pepper', 'Ginger', 'Smokey',
      'Tiger', 'Oreo', 'Charlie', 'Max', 'Bella', 'Lucy', 'Daisy', 'Molly'
    ];

    return seeds.map((seed, index) => ({
      id: `avatar-${index + 1}`,
      seed,
      url: generateAvatarUrl(seed),
      label: `Avatar ${index + 1} (${seed})`
    }));
  }, [generateAvatarUrl]);

  // Generate a random avatar
  const generateRandomAvatar = useCallback((): AvatarOption => {
    const randomSeed = Math.random().toString(36).substring(2, 15);
    return {
      id: `random-${Date.now()}`,
      seed: randomSeed,
      url: generateAvatarUrl(randomSeed),
      label: `Random Avatar`
    };
  }, [generateAvatarUrl]);

  // Select an avatar
  const selectAvatar = useCallback((avatarId: string) => {
    setSelectedAvatarId(avatarId);
  }, []);

  // Get selected avatar data
  const selectedAvatar = useMemo(() => {
    return avatarOptions.find(avatar => avatar.id === selectedAvatarId) || null;
  }, [avatarOptions, selectedAvatarId]);

  // Get avatar URL by seed (useful for displaying stored avatars)
  const getAvatarUrlBySeed = useCallback((seed: string): string => {
    return generateAvatarUrl(seed);
  }, [generateAvatarUrl]);

  return {
    // Avatar options
    avatarOptions,
    
    // Selected avatar state
    selectedAvatarId,
    selectedAvatar,
    
    // Actions
    selectAvatar,
    generateRandomAvatar,
    generateAvatarUrl,
    getAvatarUrlBySeed,
  };
}

/**
 * Utility function to generate avatar URL outside of component
 */
export function generateDicebearAvatarUrl(seed: string, size: number = 120): string {
  const baseUrl = 'https://api.dicebear.com/7.x/adventurer/svg';
  const params = new URLSearchParams({
    seed,
    size: size.toString(),
    backgroundColor: 'transparent',
    radius: '20'
  });
  
  return `${baseUrl}?${params.toString()}`;
}
